"use client";
import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeSettings } from "@/utils/theme/Theme";
import { useSelector } from "react-redux";
import { AppRouterCacheProvider } from "@mui/material-nextjs/v14-appRouter";
import { AppState } from "@/store/store";
import "@/utils/i18n";
import "@/app/api/index";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

/**
 * Custom HTML5 backend factory that handles re-initialization gracefully.
 * This prevents "Cannot have two HTML5 backends" errors during HMR/Strict Mode.
 */
const ResilientHTML5Backend = (manager: any, globalContext?: any, configuration?: any) => {
  // Clear stale backend context BEFORE creating the backend
  if (typeof window !== 'undefined') {
    delete (window as any).__reactDndContext;
  }

  const backend = HTML5Backend(manager, globalContext, configuration);

  // Also patch setup as a safety net
  const originalSetup = backend.setup;
  backend.setup = function() {
    if (typeof window !== 'undefined') {
      delete (window as any).__reactDndContext;
    }
    return originalSetup.call(this);
  };

  // Patch teardown to ensure cleanup
  const originalTeardown = backend.teardown;
  backend.teardown = function() {
    const result = originalTeardown.call(this);
    if (typeof window !== 'undefined') {
      delete (window as any).__reactDndContext;
    }
    return result;
  };

  return backend;
};

const MyApp = ({ children }: { children: React.ReactNode }) => {
  const theme = ThemeSettings();
  const customizer = useSelector((state: AppState) => state.customizer);

  return (
    <AppRouterCacheProvider options={{ enableCssLayer: true }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <DndProvider backend={ResilientHTML5Backend}>
          {children}
        </DndProvider>
      </ThemeProvider>
    </AppRouterCacheProvider>
  );
};

export default MyApp;
